import React from 'react';
import {
  Dashboard as DashboardIcon,
  AddBox,
  Inbox as InboxIcon,
  HourglassEmpty,
  CheckCircle,
  AccountBalance,
  Assessment,
  Schedule as ScheduleIcon,
  People,
  Business,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  AccountTree as AccountTreeIcon,
  Computer as ComputerIcon,
  ShoppingBasket,
} from '@mui/icons-material';

// Import page components
const Dashboard = React.lazy(() => import('../pages/Dashboard'));
const CreatePR = React.lazy(() => import('../pages/CreatePR'));
const CreatePO = React.lazy(() => import('../pages/CreatePO'));
const Inbox = React.lazy(() => import('../pages/Inbox'));
const InProgress = React.lazy(() => import('../pages/InProgress'));
const Completed = React.lazy(() => import('../pages/Completed'));
const Budget = React.lazy(() => import('../pages/Budget'));
const Reports = React.lazy(() => import('../pages/Reports'));
const ScheduleJobs = React.lazy(() => import('../pages/ScheduleJobs'));
const UserManagement = React.lazy(() => import('../pages/UserManagement'));
const CompanyManagement = React.lazy(() => import('../pages/CompanyManagement'));
const GeneralSettings = React.lazy(() => import('../pages/GeneralSettings'));
const ApprovalFlowSettings = React.lazy(() => import('../pages/ApprovalFlowSettings'));
const Projects = React.lazy(() => import('../pages/Projects'));
const RoleManagement = React.lazy(() => import('../pages/RoleManagement'));
const CostCenterMapping = React.lazy(() => import('../pages/CostCenterMapping'));
const TransactionSync = React.lazy(() => import('../pages/TransactionSync'));
const SystemMonitor = React.lazy(() => import('../pages/SystemMonitor'));

export interface ModuleItem {
  key: string;
  path: string;
  label: string;
  icon?: React.ReactNode;
  component: React.ComponentType;
  moduleKey: string;
  order: number;
  roles?: string[];
  hideFromMenu?: boolean;
}

export const MODULES: ModuleItem[] = [
  { key: 'operations-history', path: '/settings/operations-history', label: 'Operations history', icon: <Assessment />, component: React.lazy(() => import('../pages/OperationsHistory')), moduleKey: 'operations-history', order: 195, roles: ['Admin'] },
  {
    key: 'dashboard',
    path: '/dashboard',
    label: 'Dashboard',
    icon: <DashboardIcon />,
    component: Dashboard,
    moduleKey: 'dashboard',
    order: 10,
  },
  {
    key: 'create-pr',
    path: '/create-pr',
    label: 'Create PR',
    icon: <AddBox />,
    component: CreatePR,
    moduleKey: 'create-pr',
    order: 20,
  },
  {
    key: 'create-po',
    path: '/create-po',
    label: 'Create PO',
    icon: <ShoppingBasket />,
    component: CreatePO,
    moduleKey: 'create-po',
    order: 25,
  },
  {
    key: 'inbox',
    path: '/inbox',
    label: 'Inbox',
    icon: <InboxIcon />,
    component: Inbox,
    moduleKey: 'inbox',
    order: 30,
  },
  {
    key: 'in-progress',
    path: '/in-progress',
    label: 'In Progress',
    icon: <HourglassEmpty />,
    component: InProgress,
    moduleKey: 'in-progress',
    order: 40,
  },
  {
    key: 'completed',
    path: '/completed',
    label: 'Completed',
    icon: <CheckCircle />,
    component: Completed,
    moduleKey: 'completed',
    order: 50,
  },
  {
    key: 'budget',
    path: '/budget',
    label: 'Budget',
    icon: <AccountBalance />,
    component: Budget,
    moduleKey: 'budget',
    order: 60,
  },
  {
    key: 'reports',
    path: '/reports',
    label: 'Reports Overview',
    icon: <Assessment />,
    component: Reports,
    moduleKey: 'reports',
    order: 70,
  },
  {
    key: 'report-logs', path: '/reports/logs', label: 'Log Reports', icon: <Assessment />, component: Reports, moduleKey: 'report-logs', order: 71,
  },
  {
    key: 'report-approval-tracking', path: '/reports/approval-tracking', label: 'Approval Tracking', icon: <Assessment />, component: Reports, moduleKey: 'report-approval-tracking', order: 72,
  },
  {
    key: 'report-pr-detailed', path: '/reports/pr-detailed', label: 'PR Detailed Report', icon: <Assessment />, component: Reports, moduleKey: 'report-pr-detailed', order: 73,
  },
  // Admin modules
  {
    key: 'schedule-jobs',
    path: '/schedule-jobs',
    label: 'Schedule Jobs',
    icon: <ScheduleIcon />,
    component: ScheduleJobs,
    roles: ['Admin', 'FinanceManager', 'GeneralManager'],
    moduleKey: 'schedule-jobs',
    order: 75,
  },
  {
    key: 'users',
    path: '/users',
    label: 'User Management',
    icon: <People />,
    component: UserManagement,
    roles: ['Admin', 'FinanceManager', 'GeneralManager'],
    moduleKey: 'users',
    order: 80,
  },
  {
    key: 'companies',
    path: '/companies',
    label: 'Company Management',
    icon: <Business />,
    component: CompanyManagement,
    roles: ['Admin', 'FinanceManager', 'GeneralManager'],
    moduleKey: 'companies',
    order: 90,
  },
  {
    key: 'general-settings',
    path: '/settings/general',
    label: 'General Settings',
    icon: <SettingsIcon />,
    component: GeneralSettings,
    moduleKey: 'general-settings',
    roles: ['Admin', 'FinanceManager', 'GeneralManager'],
    order: 89,
  },
  {
    key: 'approval-flow-settings',
    path: '/settings/approval-flow',
    label: 'Approval Flow',
    icon: <SettingsIcon />,
    component: ApprovalFlowSettings,
    moduleKey: 'approval-flow-settings',
    roles: ['Admin', 'GeneralManager'],
    order: 96,
  },
  {
    key: 'projects',
    path: '/settings/projects',
    label: 'Project Management',
    icon: <Business />,
    component: Projects,
    moduleKey: 'projects',
    roles: ['Admin', 'FinanceManager', 'GeneralManager'],
    order: 97,
  },
  {
    key: 'role-management',
    path: '/settings/role-management',
    label: 'Role Management',
    icon: <SecurityIcon />,
    component: RoleManagement,
    moduleKey: 'role-management',
    roles: ['Admin'],
    order: 98,
  },
  {
    key: 'cost-center-mapping',
    path: '/settings/cost-center-mapping',
    label: 'Cost Center Mapping',
    icon: <AccountTreeIcon />,
    component: CostCenterMapping,
    moduleKey: 'cost-center-mapping',
    roles: ['Admin', 'FinanceManager', 'GeneralManager'],
    order: 99,
  },
  {
    key: 'transaction-sync',
    path: '/settings/transaction-sync',
    label: 'Transaction Sync',
    icon: <SettingsIcon />,
    component: TransactionSync,
    moduleKey: 'transaction-sync',
    roles: ['Admin', 'FinanceManager'],
    order: 100,
  },
  {
    key: 'system-monitor',
    path: '/settings/system-monitor',
    label: 'System Monitor',
    icon: <ComputerIcon />,
    component: SystemMonitor,
    moduleKey: 'system-monitor',
    roles: ['Admin'],
    order: 101,
  },
];

// Menu grouping for sidebar rendering (routes still come from MODULES)
export interface MenuGroup {
  key: string;
  label: string;
  icon?: React.ReactNode;
  children: ModuleItem[];
  order?: number;
}

// Define grouped menus as requested: group Inbox, In Progress, Completed; and Settings with Company/User/Schedule Jobs
export const MENU_GROUPS: MenuGroup[] = [
  {
    key: 'group-work',
    label: 'Work Flow',
    icon: <Inbox />,
    children: [
      MODULES.find((m) => m.label === 'Inbox'),
      MODULES.find((m) => m.label === 'In Progress'),
      MODULES.find((m) => m.label === 'Completed'),
    ].filter(Boolean) as ModuleItem[],
    order: 15,
  },
  {
    key: 'group-dashboard',
    label: 'Dashboard',
    icon: <DashboardIcon />,
    children: [MODULES.find((m) => m.key === 'dashboard')].filter(Boolean) as ModuleItem[],
    order: 10,
  },
  {
    key: 'group-reports',
    label: 'Reports',
    icon: <Assessment />,
    children: [
      MODULES.find((m) => m.key === 'reports'),
      MODULES.find((m) => m.key === 'report-logs'),
      MODULES.find((m) => m.key === 'report-approval-tracking'),
      MODULES.find((m) => m.key === 'report-pr-detailed'),
    ].filter(Boolean) as ModuleItem[],
    order: 70,
  },
  {
    key: 'group-settings',
    label: 'Settings',
    icon: <SettingsIcon />,
    children: [
      MODULES.find((m) => m.label === 'General Settings'),
      MODULES.find((m) => m.label === 'Company Management'),
      MODULES.find((m) => m.label === 'User Management'),
      MODULES.find((m) => m.label === 'Schedule Jobs'),
      MODULES.find((m) => m.label === 'Approval Flow'),
      MODULES.find((m) => m.label === 'Project Management'),
      MODULES.find((m) => m.label === 'Role Management'),
      MODULES.find((m) => m.label === 'Cost Center Mapping'),
      MODULES.find((m) => m.label === 'Transaction Sync'),
      MODULES.find((m) => m.label === 'System Monitor'),
      MODULES.find((m) => m.key === 'operations-history'),
    ].filter(Boolean) as ModuleItem[],
    order: 80,
  },
];

export default MODULES;
